import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetSceneContents,
  flattenSceneContents,
  useGetNextSceneContent
} from '../../../domains/scene/ui/hooks/useGetSceneContents';
import type { components } from '../../../types/schema';

type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];

export const useSceneChatList = (sceneId: string | undefined, scenePreviewId: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursorFromUrl = searchParams.get('cursor');
  const skipNextCursorResetRef = useRef(false);

  const [effectiveCursor, setEffectiveCursor] = useState<string | null>(cursorFromUrl);
  const isBeforeMode = effectiveCursor != null;

  const {
    data,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isFetchingPreviousPage,
    isLoading: isBaseLoading
  } = useGetSceneContents(
    sceneId as string,
    scenePreviewId,
    isBeforeMode
      ? { cursor: effectiveCursor, direction: 'before', limit: 10 }
      : { direction: 'after', limit: 1 }
  );

  const { mutateAsync: fetchNextOne, isLoading: isFetchingNextOne } = useGetNextSceneContent(
    sceneId as string,
    scenePreviewId,
    { limit: 1 }
  );

  const [forwardCursor, setForwardCursor] = useState<string | null>(cursorFromUrl);
  const [afterContents, setAfterContents] = useState<SceneContent[]>([]);

  useEffect(() => {
    if (!sceneId) {
      setAfterContents([]);
      setForwardCursor(null);
      setEffectiveCursor(null);
      return;
    }

    if (skipNextCursorResetRef.current) {
      skipNextCursorResetRef.current = false;
      return;
    }

    setAfterContents([]);
    setForwardCursor(cursorFromUrl);
    setEffectiveCursor(cursorFromUrl);
  }, [cursorFromUrl, sceneId]);

  useEffect(() => {
    if (isBeforeMode) return;
    if (forwardCursor != null) return;

    const firstPage = data?.pages?.[0];
    const next = firstPage?.meta?.pagination?.nextCursor ?? null;
    if (next != null) setForwardCursor(next);
  }, [data?.pages, forwardCursor, isBeforeMode]);

  const baseContents = flattenSceneContents(data) ?? [];
  const contents = [...baseContents, ...afterContents];

  const pages = data?.pages ?? [];
  const initialPage = isBeforeMode ? pages[pages.length - 1] : pages[0];
  const totalCount = initialPage?.meta?.pagination?.totalCount ?? 0;
  const maxSequenceLoaded =
    contents.length > 0 ? Math.max(...contents.map((c) => c.sequence ?? 0)) : 0;
  const remainingPreviewsCount =
    totalCount > 0 ? Math.max(0, totalCount - maxSequenceLoaded) : null;

  const loadMoreBefore = () => {
    if (!isBeforeMode) return;
    const hasMore = data?.pages?.[0]?.meta?.pagination?.hasMore;
    if (hasMore === false) return;
    if (isFetching || isFetchingPreviousPage || !hasPreviousPage) return;

    fetchPreviousPage();
  };

  const handleOpenNextPreview = async () => {
    if (!sceneId) return;
    if (!forwardCursor) return;
    if (isFetchingNextOne) return;

    try {
      const res = await fetchNextOne({ cursor: forwardCursor });
      const newNextCursor = res?.meta?.pagination?.nextCursor ?? null;
      const newContents = (res?.sceneContents ?? []) as SceneContent[];

      setAfterContents((prev) => appendUniqueAfterContents(baseContents, prev, newContents));
      setForwardCursor(newNextCursor);

      if (newNextCursor) {
        skipNextCursorResetRef.current = true;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('cursor', newNextCursor);
            return next;
          },
          { replace: true }
        );
      }
    } catch {}
  };

  const isLoading = isBaseLoading;

  return {
    contents,
    isBeforeMode,
    isLoading,
    isFetchingPreviousPage,
    isFetchingNextOne,
    forwardCursor,
    loadMoreBefore,
    handleOpenNextPreview,
    baseContentsLength: baseContents.length,
    remainingPreviewsCount,
    totalCount,
    resetKey: `${sceneId ?? ''}-${effectiveCursor ?? ''}`
  };
};

export const getContentKey = (content: SceneContent): string => {
  const stableId = getStableContentId(content);
  if (stableId) return stableId;
  if (typeof content.sequence === 'number') return `seq-${content.sequence}`;
  return 'content-unknown';
};

const getStableContentId = (content: SceneContent): string | null => {
  if ('sceneThreadId' in content && content.sceneThreadId) return content.sceneThreadId;
  if ('sceneChatId' in content && (content as { sceneChatId?: string }).sceneChatId) {
    return (content as { sceneChatId: string }).sceneChatId;
  }
  return null;
};

// 중복 제거 및 병합 로직
const appendUniqueAfterContents = (
  base: SceneContent[],
  afterPrev: SceneContent[],
  toAppend: SceneContent[]
): SceneContent[] => {
  const seen = new Set<string>();

  // 기존 base와 이미 로드된 after 데이터의 ID 수집
  for (const item of [...base, ...afterPrev]) {
    const id = getStableContentId(item);
    if (id) seen.add(id);
  }

  const next = [...afterPrev];
  for (const item of toAppend) {
    const id = getStableContentId(item);
    // 이미 존재하는 ID라면 건너뜀 (중복 방지)
    if (id && seen.has(id)) continue;

    if (id) seen.add(id);
    next.push(item);
  }

  return next;
};
